// ── AdminLogger.js ────────────────────────────────────────────────────
// Централізоване логування дій адміна та юзерів.
// Використовуй: import { logAction, A, E } from '../../AdminLogger';
// ─────────────────────────────────────────────────────────────────────
import { API } from '../config/api';

// ── Action types ──────────────────────────────────────────────────────
export const A = {
  CREATE:   'create',
  UPDATE:   'update',
  DELETE:   'delete',
  LOGIN:    'login',
  LOGOUT:   'logout',
  EXPORT:   'export',
  ERROR:    'error',
  SECURITY: 'security',
  FINANCE:  'finance',
  SYSTEM:   'system',
  IMPORT:   'import',
  PRINT:    'print',
};

// ── Entity types ──────────────────────────────────────────────────────
export const E = {
  INVOICE:      'invoice',
  INVOICE_LINE: 'invoice_line',
  CUSTOMER:     'customer',
  ITEM:         'item',
  PRICE:        'price',
  EXPENSE:      'expense',
  SETTINGS:     'settings',
  POS:          'pos',
  POS_SALE:     'pos_sale',
  ORDER:        'order',
  USER:         'user',
  PROFILE:      'profile',
  PRICING:      'pricing',
  SESSION:      'session',
  CART:         'cart',
  TODO:         'todo',
  CASHBACK:     'cashback',
};

let _email    = '';
let _userName = '';
let _isAdmin  = false;

export function setLogEmail(email)              { _email    = email; }
export function setLogUser(userName, isAdmin)   { _userName = userName || ''; _isAdmin = !!isAdmin; }

function getDevice() {
  try {
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    if (/Android/.test(ua))          return 'Android';
    if (/Mac/.test(ua))              return 'Mac';
    if (/Windows/.test(ua))          return 'Windows';
    return 'Web';
  } catch { return 'Web'; }
}

function getPage() {
  try { return window.location.pathname.replace('/', '') || 'home'; } catch { return ''; }
}

/**
 * logAction(action, entity, entityId, summary, detail?, options?)
 *
 * options: {
 *   status:        'success' | 'failed'   default: 'success'
 *   field_changed: string
 *   old_value:     string
 *   new_value:     string
 *   page:          string   (auto-detected if omitted)
 *   metadata:      object
 * }
 */
export async function logAction(action, entity, entityId, summary, detail = null, options = {}) {
  const email = _email || (() => { try { return localStorage.getItem('userEmail') || ''; } catch { return ''; } })();
  if (!email || !API.logs) return;

  try {
    await fetch(API.logs, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action:        'log',
        action_type:   action,
        entity,
        entity_id:     String(entityId || ''),
        summary:       String(summary  || '').slice(0, 500),
        detail,
        admin_email:   email,
        user_name:     _userName,
        is_admin:      _isAdmin ? 1 : 0,
        device:        getDevice(),
        status:        options.status        || 'success',
        field_changed: options.field_changed || '',
        old_value:     options.old_value     != null ? String(options.old_value).slice(0, 500) : '',
        new_value:     options.new_value     != null ? String(options.new_value).slice(0, 500) : '',
        page:          options.page          || getPage(),
        metadata:      options.metadata      || null,
      }),
    });
  } catch {
    // Silent fail — logging must never break the app
  }
}

// ── Shorthand helpers ─────────────────────────────────────────────────
export const log = {

  // ── Auth ─────────────────────────────────────────────────────────
  loginSuccess: (email) =>
    logAction(A.LOGIN, E.SESSION, email, `Login: ${email}`),

  loginFailed: (email, reason) =>
    logAction(A.LOGIN, E.SESSION, email, `Login failed: ${email} — ${reason||'wrong password'}`,
      null, { status: 'failed' }),

  logout: (email) =>
    logAction(A.LOGOUT, E.SESSION, email, `Logout: ${email}`),

  // ── Invoices ──────────────────────────────────────────────────────
  invoiceCreate: (inv) =>
    logAction(A.CREATE, E.INVOICE, inv?.invoiceNumber||inv?.id,
      `Created ${inv?.invoiceNumber||'#new'} · ${inv?.customerName||'?'} · $${inv?.total||0}`),

  invoiceUpdate: (inv, changes) =>
    logAction(A.UPDATE, E.INVOICE, inv?.invoiceNumber||inv?.id,
      `${inv?.invoiceNumber||inv?.id} updated`, changes),

  invoiceDelete: (inv) =>
    logAction(A.DELETE, E.INVOICE, inv?.invoiceNumber||inv?.id,
      `Deleted ${inv?.invoiceNumber||inv?.id} · ${inv?.customerName||'?'}`),

  invoiceStatus: (inv, oldStatus, newStatus) =>
    logAction(A.FINANCE, E.INVOICE, inv?.invoiceNumber||inv?.id,
      `${inv?.invoiceNumber||inv?.id} status: ${oldStatus} → ${newStatus} · ${inv?.customerName||'?'}`,
      null, { field_changed: 'status', old_value: oldStatus, new_value: newStatus }),

  invoicePrint: (inv) =>
    logAction(A.PRINT, E.INVOICE, inv?.invoiceNumber||inv?.id,
      `Printed/Downloaded: ${inv?.invoiceNumber||inv?.id}`),

  // ── Invoice lines ─────────────────────────────────────────────────
  lineAdd: (invoiceNum, line) =>
    logAction(A.CREATE, E.INVOICE_LINE, invoiceNum,
      `${invoiceNum}: + ${line?.itemName||line?.itemId} × ${line?.qty} @ $${line?.price||0}`),

  lineUpdate: (invoiceNum, line, field, oldVal, newVal) =>
    logAction(A.UPDATE, E.INVOICE_LINE, invoiceNum,
      `${invoiceNum}: edited ${line?.itemName||line?.itemId}`, line,
      { field_changed: field, old_value: oldVal, new_value: newVal }),

  lineDelete: (invoiceNum, name) =>
    logAction(A.DELETE, E.INVOICE_LINE, invoiceNum,
      `${invoiceNum}: removed ${name||'item'}`),

  // ── Customers ─────────────────────────────────────────────────────
  customerCreate: (cust) =>
    logAction(A.CREATE, E.CUSTOMER, cust?.name||cust?.id,
      `Created customer: ${cust?.name||'?'} · ${cust?.email||''}`),

  customerUpdate: (cust, field, oldVal, newVal) =>
    logAction(A.UPDATE, E.CUSTOMER, cust?.name||cust?.id,
      `Updated ${cust?.name||cust?.id}: ${field} changed`,
      null, { field_changed: field, old_value: oldVal, new_value: newVal }),

  customerDelete: (cust) =>
    logAction(A.DELETE, E.CUSTOMER, cust?.name||cust?.id,
      `Deleted customer: ${cust?.name||'?'} · ${cust?.email||''}`),

  // ── Items / Products ──────────────────────────────────────────────
  itemCreate: (item) =>
    logAction(A.CREATE, E.ITEM, item?.name||item?.id,
      `Created item: ${item?.name||'?'} ${item?.size||''}`),

  itemUpdate: (item, field, oldVal, newVal) =>
    logAction(A.UPDATE, E.ITEM, item?.name||item?.id,
      `Updated ${item?.name||'?'}: ${field} ${oldVal} → ${newVal}`,
      null, { field_changed: field, old_value: oldVal, new_value: newVal }),

  itemDelete: (item) =>
    logAction(A.DELETE, E.ITEM, item?.name||item?.id,
      `Deleted: ${item?.name||'?'} ${item?.size||''}`),

  // ── Prices ────────────────────────────────────────────────────────
  priceSet: (itemId, oldPrice, newPrice, name) =>
    logAction(A.FINANCE, E.PRICE, name||itemId,
      `Price: ${name||itemId} $${oldPrice} → $${newPrice}`,
      null, { field_changed: 'price', old_value: `$${oldPrice}`, new_value: `$${newPrice}` }),

  priceClear: (itemId, name) =>
    logAction(A.DELETE, E.PRICE, name||itemId,
      `Price cleared: ${name||itemId}`),

  // ── Expenses ──────────────────────────────────────────────────────
  expenseCreate: (exp) =>
    logAction(A.CREATE, E.EXPENSE, exp?.id,
      `Expense: $${exp?.amount} · ${exp?.category||'?'} · ${exp?.notes||''}`),

  expenseUpdate: (exp, field, oldVal, newVal) =>
    logAction(A.UPDATE, E.EXPENSE, exp?.id,
      `Expense updated: ${field} ${oldVal} → ${newVal}`,
      null, { field_changed: field, old_value: oldVal, new_value: newVal }),

  expenseDelete: (exp) =>
    logAction(A.DELETE, E.EXPENSE, exp?.id,
      `Deleted expense $${exp?.amount} · ${exp?.category||'?'}`),

  // ── Settings ──────────────────────────────────────────────────────
  settingUpdate: (key, oldVal, newVal) =>
    logAction(A.SYSTEM, E.SETTINGS, key,
      `Settings: "${key}" changed`,
      null, { field_changed: key, old_value: String(oldVal||'').slice(0,80), new_value: String(newVal||'').slice(0,80) }),

  // ── POS ───────────────────────────────────────────────────────────
  posCreate: (pos) =>
    logAction(A.CREATE, E.POS, pos?.inv_num||pos?.id,
      `Created POS: ${pos?.inv_num||pos?.id}`),

  posUpdate: (pos) =>
    logAction(A.UPDATE, E.POS, pos?.inv_num||pos?.id,
      `Updated POS: ${pos?.inv_num||pos?.id}`),

  posDelete: (pos) =>
    logAction(A.DELETE, E.POS, pos?.inv_num||pos?.id,
      `Deleted POS: ${pos?.inv_num||pos?.id}`),

  posSale: (sale, posInvNum) =>
    logAction(A.CREATE, E.POS_SALE, sale?.inv_num||sale?.id,
      `POS sale ${sale?.inv_num||''}: $${sale?.total||0} · ${sale?.payment||'cash'}`,
      null, { metadata: { pos_inv: posInvNum, items: sale?.items_json } }),

  // ── Cart / Order ──────────────────────────────────────────────────
  orderCreate: (invoiceId, itemCount, total) =>
    logAction(A.CREATE, E.ORDER, invoiceId,
      `Store order: ${invoiceId} · ${itemCount} items · $${total}`),

  // ── Todo ──────────────────────────────────────────────────────────
  todoCheck: (item, oldVal, newVal) =>
    logAction(A.UPDATE, E.TODO, item?.id||item?.rowId,
      `Todo: "${item?.itemName||item?.name||'?'}" → ${newVal}`,
      null, { field_changed: 'check_done', old_value: oldVal, new_value: newVal }),

  // ── Profile ───────────────────────────────────────────────────────
  profileUpdate: (email, field, oldVal, newVal) =>
    logAction(A.UPDATE, E.PROFILE, email,
      `Profile updated: ${field}`,
      null, { field_changed: field, old_value: oldVal, new_value: newVal }),

  // ── Security ──────────────────────────────────────────────────────
  securityViolation: (email, description) =>
    logAction(A.SECURITY, E.USER, email,
      `Security: ${description}`, null, { status: 'failed' }),

  // ── Export / Print ────────────────────────────────────────────────
  exportPDF: (entityId, entityType) =>
    logAction(A.EXPORT, entityType||E.INVOICE, entityId,
      `PDF exported: ${entityId}`),

  // ── System ────────────────────────────────────────────────────────
  systemAction: (description, metadata) =>
    logAction(A.SYSTEM, 'system', 'auto',
      description, null, { metadata }),
};

// ── Cashback helpers ─────────────────────────────────────────────────
export const LogCB = {
  give:   (inv, amount, percent) =>
    logAction(A.FINANCE, E.CASHBACK, inv?.invoiceNumber||inv?.id,
      `Cashback given: $${amount} (${percent}%) for ${inv?.invoiceNumber||inv?.id}`,
      { amount, percent, invoice_id: inv?.id, customer_id: inv?.customerId }),

  payout: (customerName, amount, method) =>
    logAction(A.FINANCE, E.CASHBACK, customerName,
      `Cashback payout: $${amount} via ${method} to ${customerName}`,
      { amount, method }),

  adjust: (customerName, amount, note) =>
    logAction(amount > 0 ? A.CREATE : A.DELETE, E.CASHBACK, customerName,
      `Manual cashback adjust: ${amount > 0 ? '+' : ''}$${amount} for ${customerName}`,
      { amount, note }),

  edit:   (id, amount, note) =>
    logAction(A.UPDATE, E.CASHBACK, String(id),
      `Cashback transaction #${id} edited: $${amount}`,
      { id, amount, note }),

  delete: (id, type, customerName) =>
    logAction(A.DELETE, E.CASHBACK, String(id),
      `Cashback transaction #${id} deleted (${type}) for ${customerName}`,
      { id, type }),
};
